// Products Manager Class for the Products Section
// Handles business logic and state management for products
// Follows the Manager pattern adapted from LearnManager

import { Manager } from '../../../core';
import { logger } from '../../../core';

export class MarketplaceManager extends Manager {
  constructor() {
    super();
    this.products = [];
    this.featuredProducts = [];
    this.categories = [];
    this.searchTerm = '';
    this.isLoading = false;
    this.includeInactive = false;
  }

  /**
   * Initialize global variables and setup
   */
  async initGlobalVariables() {
    // Navigation manager will be connected via the React hook adapter
  }

  /**
   * Initialize states and data
   */
  async initStates() {
    logger.log(
      {
        tags: 'manager|products|init',
        color1: 'cyan',
      },
      'Initializing MarketplaceSection manager states'
    );

    try {
      // Load initial content
      await this.loadContent();

      logger.log(
        {
          tags: 'manager|products|ready',
          color1: 'lime',
        },
        `MarketplaceSection manager initialized with ${this.products.length} products`
      );
    } catch (error) {
      logger.error(
        {
          tags: 'manager|products|error',
          color1: 'red',
          color2: 'orange',
          includeSource: true,
        },
        'Failed to initialize MarketplaceSection manager states:',
        error
      );

      throw error;
    }
  }

  /**
   * Load featured products content from backend API
   */
  async loadContent(filters = {}) {
    this.isLoading = true;

    try {
      logger.log(
        {
          tags: 'manager|products|api',
          color1: 'blue',
        },
        'Loading products content from API'
      );

      // Load categories from API (TODO: implement API endpoint)
      this.categories = [];

      // Featured products - use admin endpoint when includeInactive (admin view)
      const featuredResponse = this.includeInactive
        ? await this.getAdminFeaturedProductsFromAPI(1, 200, true)
        : await this.getFeaturedProductsFromAPI(1, 200, filters);
      this.featuredProducts = featuredResponse.products || [];

      // Keep full products list empty by default; searches should call backend endpoint
      this.products = [];

      logger.log(
        {
          tags: 'manager|products|success',
          color1: 'lime',
        },
        `Loaded ${this.featuredProducts.length} featured products from API`
      );
    } catch (error) {
      logger.error(
        {
          tags: 'manager|products|error',
          color1: 'red',
        },
        'Failed to load products content:',
        error
      );
      // Leave data empty on API error - components will handle empty states
      this.products = [];
      this.featuredProducts = [];
      this.categories = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Set includeInactive for admin view - when true, load featured/search includes inactive products
   */
  setIncludeInactive(value) {
    this.includeInactive = Boolean(value);
  }

  /**
   * Get admin featured products (includes inactive when includeInactive=true)
   */
  async getAdminFeaturedProductsFromAPI(page = 1, limit = 25, includeInactive = false) {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        includeInactive: includeInactive ? 'true' : 'false',
      });

      const response = await fetch(`/api/admin/products/featured?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        return { products: [], total: 0, page, limit };
      }

      return {
        products: data.products || [],
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
      };
    } catch (error) {
      logger.error('products', 'Failed to fetch admin featured products from API:', error);
      return { products: [], total: 0, page, limit };
    }
  }

  /**
   * Get featured products from backend API
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {Object} filters - Optional filters (bundle: 'bundles' | 'individual')
   */
  async getFeaturedProductsFromAPI(page = 1, limit = 25, filters = {}) {
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (filters && filters.bundle) {
        params.set('bundle', String(filters.bundle));
      }

      const response = await fetch(`/api/products/featured?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        return { products: [], total: 0, page, limit };
      }

      return {
        products: data.products || [],
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
      };
    } catch (error) {
      logger.error('products', 'Failed to fetch featured products from API:', error);
      return { products: [], total: 0, page, limit };
    }
  }

  /**
   * Get product details by ID from backend API
   */
  async getProductById(productId) {
    try {
      const response = await fetch(`/api/products/${productId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch product');
      }

      return data.product || null;
    } catch (error) {
      logger.error('products', 'Failed to fetch product by ID:', error);
      throw error;
    }
  }

  /**
   * Update product details by ID
   */
  async updateProduct(productId, updateData) {
    try {
      const response = await fetch(`/api/admin/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update product');
      }

      return data.product || null;
    } catch (error) {
      logger.error('products', 'Failed to update product:', error);
      throw error;
    }
  }

  /**
   * Search products from backend API
   * @param {string} query - Search query
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {Object} filters - Additional filters (includeInactive for admin)
   */
  async searchProductsFromAPI(query, page = 1, limit = 25, filters = {}) {
    try {
      const useAdminSearch = filters && filters.includeInactive === true;
      const params = new URLSearchParams({
        q: query || '',
        page: String(page),
        limit: String(limit),
      });

      if (filters && typeof filters === 'object') {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '' && key !== 'includeInactive') {
            params.set(key, String(value));
          }
        });
      }

      if (useAdminSearch) {
        params.set('includeInactive', 'true');
      }

      const url = useAdminSearch
        ? `/api/admin/products/search?${params.toString()}`
        : `/api/products/search?${params.toString()}`;
      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        return { products: [], total: 0, page, limit };
      }

      return {
        products: data.products || [],
        total: data.total || 0,
        page: data.page || page,
        limit: data.limit || limit,
      };
    } catch (error) {
      logger.error('products', 'Failed to search products from API:', error);
      return { products: [], total: 0, page, limit };
    }
  }

  /**
   * Set search term and update filtered results
   */
  setSearchTerm(term) {
    this.searchTerm = term;

    logger.log(
      {
        tags: 'manager|products|action',
        color1: 'blue',
      },
      `Search term updated: "${term}"`
    );
  }

  /**
   * Reorder featured products - updates featuredOrder for swapped products
   * @param {Array<{ productId: string, featuredOrder: number }>} swaps
   */
  async reorderFeaturedProducts(swaps) {
    if (!Array.isArray(swaps) || swaps.length === 0) return;

    try {
      const response = await fetch('/api/admin/products/reorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ swaps }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to reorder');
      }

      await this.loadContent();
    } catch (error) {
      logger.error('products', 'Failed to reorder featured products:', error);
      throw error;
    }
  }

  /**
   * Get featured products
   */
  getFeaturedProducts() {
    return this.featuredProducts;
  }

  /**
   * Get filtered products based on search term
   */
  getFilteredProducts() {
    const search = this.searchTerm.trim();

    if (!search) {
      return this.products;
    }

    const searchLower = search.toLowerCase();

    // Search by product name or description
    return this.products.filter(product =>
      product.name.toLowerCase().includes(searchLower) ||
      product.description.toLowerCase().includes(searchLower) ||
      product.tags.some(tag => tag.toLowerCase().includes(searchLower))
    );
  }



}